// Portfolio Administration v1.0 PR 3 — minimal feature-flag gate.
//
// The Phoenix Capital backend enforces flags server-side via
// [FeatureFlag("Name")] (returns 404 when off). We mirror that on the
// frontend for the Loan-detail route only — so we can fall back to the
// PR 0 PlaceholderPage when the rollout is paused without shipping a
// broken page to operators. The lookup reads a Vite build-time env var:
//
//   VITE_FEATURE_PORTFOLIO_ADMIN_V1=true   # dev / staging
//   VITE_FEATURE_PORTFOLIO_ADMIN_V1=false  # prod (until rollout)
//
// Unset → defaults to true so local-dev (which doesn't define the var)
// behaves like staging. This is a single-purpose helper, not a generic
// flag library.
//
// PreContributionScope (2026-06) — the platform is scoped to the
// pre-contribution period of every loan: the seller holds the note, there
// are no investors, no SPV pools, no contributed segments, and no
// reunderwriting workflow. When ON (the default in EVERY environment),
// stage-2 surfaces are hidden, not deleted: the right-size / resale /
// financial-closing wizards redirect to the loan detail page, the
// segment ledger disappears from Payment Tracking, and all reunderwriting
// surfaces (header CTA, nav group, route pages) are hidden via
// useReunderwritingV1. Phase 2 re-enables everything by building with
// VITE_FEATURE_PRE_CONTRIBUTION_SCOPE=false. This hook is the single
// resolution point — swapping it to a server-driven flag later re-enables
// every gated surface without touching the consumers.

const PORTFOLIO_ADMIN_V1_ENV = "VITE_FEATURE_PORTFOLIO_ADMIN_V1";
const PRE_CONTRIBUTION_SCOPE_ENV = "VITE_FEATURE_PRE_CONTRIBUTION_SCOPE";
// CapitalMarkets (2026-06) — the Capital Markets / SPV section is hidden by
// default during the pre-contribution period (no investors, no SPV pools).
// Unlike the flags above it defaults OFF, so an unset var hides the section;
// a build sets VITE_FEATURE_CAPITAL_MARKETS=true to reveal it (reversible, no
// code change). The Sidebar reads this to strip the "Capital Markets" group;
// the routes stay registered so deep links still resolve.
const CAPITAL_MARKETS_ENV = "VITE_FEATURE_CAPITAL_MARKETS";

function readBool(name: string, defaultValue: boolean): boolean {
  const raw = (import.meta.env as Record<string, string | undefined>)[name];
  if (raw === undefined || raw === "") return defaultValue;
  return raw.toLowerCase() === "true" || raw === "1";
}

export type FeatureFlagName =
  | "PortfolioAdminV1"
  | "PreContributionScope"
  | "CapitalMarkets";

export function useFeatureFlag(flag: FeatureFlagName): boolean {
  switch (flag) {
    case "PortfolioAdminV1":
      return readBool(PORTFOLIO_ADMIN_V1_ENV, true);
    case "PreContributionScope":
      return readBool(PRE_CONTRIBUTION_SCOPE_ENV, true);
    case "CapitalMarkets":
      return readBool(CAPITAL_MARKETS_ENV, false);
  }
}
